import React from "react";
import { useTranslation } from "react-i18next";

import { SelectOptions } from "../assets/data/options";
import { CreateSaleManageable } from "../store/sale/types";
import { getInputDateFormat } from "../utils/utility";
import {
  CommonDatePicker,
  CommonSelect,
  CommonTextField,
} from "./index";

export const SaleInfoField = ({
  id,
  values,
  errors,
  touched,
  setFieldValue,
  className,
  disabled,
  noLabels,
}: any) => {
  // Hooks
  const {
    t, i18n
  } = useTranslation();

  const getCommonDatePickerWrapperClass = (key: string) => {
    const isValidationFailed = errors && touched && errors[key] && touched[key];
    const isEmpty = values[key] ? false : true;
    return (
      "common-date-picker" +
      (isValidationFailed
        ? "--error"
        : disabled
          ? "--disabled"
          : "") +
      " common-date-picker" +
      (isEmpty ? "--empty" : "")
    );
  };

  const getPlaceholder = (id: string) => {
    if (noLabels) {
      return undefined;
    }

    switch (id) {
      case "assetType":
        return t("Select...");
      case "baFinId":
        return t("BaFinID");
      case "company":
        return t("Company");
      case "description":
        return t("Description");
      case "fee":
        return t("Fee");
      case "interestRate":
        return t("Interest");
      case "interestPayoutPeriod":
        return t("Select...");
      case "maxPurchase":
        return t("Maximum Purchase");
      case "minPurchase":
        return t("Minimum Purchase");
      case "name":
        return t("Name");
      case "sharesCap":
        return t("Supply Cap");
      default:
        return undefined;
    }
  }

  const getInitialFocusedDate = (id: string) => {
    switch (id) {
      default:
        return undefined;
    }
  }

  const getInputProps = (id: string) => {
    switch (id) {
      case "fee":
        return { min: CreateSaleManageable.minFee };
      case "interestRate":
        return { min: CreateSaleManageable.minInterestRate };
      case "sharesCap":
        return { min: CreateSaleManageable.minSharesCap };
      case "maxPurchase":
        return { min: CreateSaleManageable.minMaxPurchase };
      case "minPurchase":
        return { min: CreateSaleManageable.minMinPurchase };
      default:
        return undefined;
    }
  }

  const getLabel = (id: string) => {
    if (noLabels) {
      return undefined;
    }

    switch (id) {
      case "assetType":
        return t("Asset type");
      case "endDate":
        return t("Sale end");
      case "interestPayoutPeriod":
        return t("Interest payout period");
      case "startDate":
        return t("Sale start");
      default:
        return undefined;
    }
  }

  const getOptions = (id: string) => {
    switch (id) {
      case "assetType":
        return SelectOptions().asset;
      case "interestPayoutPeriod":
        return SelectOptions().period;
      default:
        return undefined;
    }
  }

  const getType = (id: string) => {
    switch (id) {
      case "fee":
      case "interestRate":
      case "maxPurchase":
      case "minPurchase":
      case "sharesCap":
        return "number";
      default:
        return undefined;
    }
  }

  const getMaxDate = (id: string) => {
    switch (id) {
      case "startDate":
        if (values.endDate) {
          let maxDate = new Date(values.endDate);
          maxDate.setDate(maxDate.getDate() - 1);
          return maxDate;
        } else {
          return undefined;
        }
      default:
        return undefined;
    }
  }

  const getMinDate = (id: string) => {
    switch (id) {
      case "endDate":
        if (values.startDate) {
          let minDate = new Date(values.startDate);
          minDate.setDate(minDate.getDate() + 1);
          return minDate;
        } else {
          return undefined;
        }
      default:
        return undefined;
    }
  }

  const getSetFieldValueWrapper = (id: string) => {
    switch (id) {
      case "endDate":
      case "startDate":
        return (
          id: string, value: Date
        ) => setFieldValue(
          id,
          value.toISOString()
        )
      case "sharesCap":
      case "maxPurchase":
      case "minPurchase":
        return (
          id: string, value: number
        ) => setFieldValue(
          id,
          Math.round(value)
        )
      default:
        return setFieldValue;
    }
  }

  const shouldDisableFuture = (id: string) => {
    switch (id) {
      default:
        return false;
    }
  }

  const shouldDisablePast = (id: string) => {
    switch (id) {
      case "startDate":
      case "endDate":
        return true;
      default:
        return false;
    }
  }

  // Options
  const disableFuture = shouldDisableFuture(id);
  const disablePast = shouldDisablePast(id);
  const initialFocusedDate = getInitialFocusedDate(id);
  const inputProps = getInputProps(id);
  const label = getLabel(id);
  const maxDate = getMaxDate(id);
  const minDate = getMinDate(id);
  const placeholder = getPlaceholder(id);
  const options = getOptions(id);
  const type = getType(id);
  const setFieldValueWrapper = getSetFieldValueWrapper(id);

  switch (id) {
    case "baFinId":
    case "company":
    case "description":
    case "fee":
    case "interestRate":
    case "maxPurchase":
    case "minPurchase":
    case "name":
    case "sharesCap":
      return (
        <CommonTextField
          disabled={disabled}
          id={id}
          type={type}
          label={label}
          placeholder={placeholder}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValueWrapper}
          className={className}
          inputProps={inputProps}
        />
      );
    case "assetType":
    case "interestPayoutPeriod":
      return (
        <CommonSelect
          disabled={disabled}
          id={id}
          label={label}
          placeholder={placeholder}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          options={options}
          className={className}
        />
      );
    case "startDate":
    case "endDate":
      return (
        <CommonDatePicker
          className={className + " " + getCommonDatePickerWrapperClass(id)}
          disabled={disabled}
          id={id}
          values={values}
          errors={errors}
          touched={touched}
          label={label}
          setFieldValue={setFieldValueWrapper}
          maxDate={maxDate}
          minDate={minDate}
          disableFuture={disableFuture}
          disablePast={disablePast}
          openTo="year"
          views={["year", "month", "date"]}
          format={getInputDateFormat(i18n.language)}
          initialFocusedDate={initialFocusedDate}
        />
      );
    default:
      return <></>;
  }
};
